'use client'

import { Button } from '@brew/ui/button'
import { FileTextIcon } from 'lucide-react'
import Image from 'next/image'
import { useCallback } from 'react'
import {
  BrewDialog,
  BrewDialogBody,
  BrewDialogClose,
  BrewDialogContent,
  BrewDialogFooter,
  BrewDialogHeader,
  BrewMediaFrame,
} from '@/components/ui/brew-dialog'

export type AttachmentPreviewDialogProps = {
  open: boolean
  filename: string
  mediaType?: string
  url: string
  isImage: boolean
  onOpenChange: (open: boolean) => void
}

export function AttachmentPreviewDialog({
  open,
  filename,
  mediaType,
  url,
  isImage,
  onOpenChange,
}: AttachmentPreviewDialogProps) {
  const attachmentLabel = filename || (isImage ? 'Image' : 'Attachment')

  const handlePreventAutoFocus = useCallback((event: Event) => {
    event.preventDefault()
  }, [])

  return (
    <BrewDialog open={open} onOpenChange={onOpenChange}>
      <BrewDialogContent size="md" onOpenAutoFocus={handlePreventAutoFocus}>
        <BrewDialogHeader
          title={<span className="block truncate">{attachmentLabel}</span>}
          description={
            mediaType ? (
              <span className="font-mono text-dialog-meta">{mediaType}</span>
            ) : (
              `${attachmentLabel} preview`
            )
          }
        />
        <BrewDialogBody>
          <BrewMediaFrame className="items-center overflow-hidden">
            {isImage ? (
              <Image
                alt={filename || 'attachment preview'}
                className="h-auto max-h-[min(30rem,calc(100dvh-18rem))] w-auto max-w-full object-contain"
                src={url}
                unoptimized
                width={1024}
                height={1024}
                sizes="(max-width: 768px) 100vw, 32rem"
                quality={75}
              />
            ) : (
              <span className="flex size-16 items-center justify-center rounded-2xl bg-[var(--dlg-surface)] text-[var(--dlg-muted)] shadow-[0_0_0_1px_var(--dlg-line)]">
                <FileTextIcon className="size-6" aria-hidden />
              </span>
            )}
          </BrewMediaFrame>
        </BrewDialogBody>
        <BrewDialogFooter>
          <BrewDialogClose asChild>
            <Button type="button">Done</Button>
          </BrewDialogClose>
        </BrewDialogFooter>
      </BrewDialogContent>
    </BrewDialog>
  )
}
